import type { InstallSource } from "@redotech/account-common/install-source";
import {
  resolveFirstInstallOnboardingProduct,
  type FirstInstallOnboardingProduct,
} from "@redotech/redo-model/onboarding";
import type { FirstInstallStep } from "./first-install-flow";

/**
 * Decides whether the Continue button's click handler should synchronously
 * pre-open a blank tab so the post-finishOnboarding redirect to
 * setup.getredo.com survives Chrome/Firefox/Safari popup blockers.
 *
 * Pre-open only when this click will trigger onboarding completion AND the
 * resulting destination is likely to be the external onboarding app:
 *  - Click must finish the flow. On non-product flows (vertical app installs)
 *    that's the attribution step; otherwise it's the product step.
 *  - Products with in-app destinations don't pre-open an external tab.
 *    Chargebacks resolve to /finance/disputes, and Withdrawals resolves to
 *    the merchant app's withdrawals setup modal.
 *  - Shopify test stores have shouldRedirect suppressed, so no tab is needed.
 */
export function shouldPreOpenExternalTab(args: {
  installSource: InstallSource | null | undefined;
  isShopifyTestStore: boolean;
  productSelectionRequired: boolean;
  selectedProduct: FirstInstallOnboardingProduct | null;
  step: FirstInstallStep | null;
}): boolean {
  if (args.isShopifyTestStore) {
    return false;
  }
  const finishingStep = args.productSelectionRequired
    ? "product"
    : "attribution";
  if (args.step !== finishingStep) {
    return false;
  }
  const effectiveProduct = resolveFirstInstallOnboardingProduct({
    installSource: args.installSource,
    selectedProduct: args.selectedProduct ?? undefined,
  });
  if (args.productSelectionRequired && !effectiveProduct) {
    return false;
  }
  if (
    effectiveProduct === "chargebacks" ||
    effectiveProduct === "withdrawals"
  ) {
    return false;
  }
  return true;
}
